import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';

import { useAuth } from '@/contexts/AuthContext';

import './AccountNudgeDialog.css';

export type AccountNudgeDialogProps = {
  open: boolean;
  /** Email the visitor used for the anonymous request — pre-fills signup. */
  email: string;
  /** Full name the visitor used — split into first/last for the signup form. */
  fullName: string;
  onClose: () => void;
};

const PREFILL_STORAGE_KEY = 'ddm.signupPrefill';

// Non-blocking invitation shown after a SUCCESSFUL anonymous Express Interest /
// Request Callback (ADR-053, acceptance criterion v1). It never appears for an
// authenticated user (DealPage only opens it on the anonymous branch; the
// auth-status guard below is defence in depth). "Create an account" carries the
// email + name to /signup WITHOUT putting PII in the URL: the values go through
// sessionStorage (same pattern as ADR-048 ddm.postAuthRedirect) and only the
// `redirect` back to this deal stays in the query string.
export function AccountNudgeDialog({ open, email, fullName, onClose }: AccountNudgeDialogProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const auth = useAuth();
  const dismissButtonRef = useRef<HTMLButtonElement>(null);

  const isOpen = open && auth.status !== 'authenticated';

  useEffect(() => {
    if (!isOpen) return;
    dismissButtonRef.current?.focus();
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const handleCreateAccount = () => {
    const trimmed = fullName.trim();
    const parts = trimmed.length > 0 ? trimmed.split(/\s+/) : [];
    const firstName = parts[0] ?? '';
    const lastName = parts.slice(1).join(' ');
    try {
      sessionStorage.setItem(PREFILL_STORAGE_KEY, JSON.stringify({ email, firstName, lastName }));
    } catch {
      // Private-mode / storage-disabled: fall through to a blank signup form.
    }
    const returnTo = `${location.pathname}${location.search}`;
    navigate(`/signup?redirect=${encodeURIComponent(returnTo)}`);
  };

  return (
    <div className="confirm-modal__backdrop" role="presentation" onClick={onClose}>
      <div
        className="confirm-modal account-nudge-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="account-nudge-title"
        aria-describedby="account-nudge-body"
        onClick={(event) => event.stopPropagation()}
      >
        <h2 id="account-nudge-title" className="confirm-modal__title">
          {t('deal.nudge.title')}
        </h2>
        <p id="account-nudge-body" className="confirm-modal__body">
          {t('deal.nudge.body')}
        </p>
        <div className="confirm-modal__actions">
          <button
            ref={dismissButtonRef}
            type="button"
            onClick={onClose}
            className="confirm-modal__btn confirm-modal__btn--cancel"
          >
            {t('deal.nudge.dismiss')}
          </button>
          <button
            type="button"
            onClick={handleCreateAccount}
            className="confirm-modal__btn confirm-modal__btn--confirm"
          >
            {t('deal.nudge.cta')}
          </button>
        </div>
      </div>
    </div>
  );
}
